// src/components/CameraCapture.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    IconButton,
    Typography,
    CircularProgress,
    alpha
} from '@mui/material';
import {
    Close as CloseIcon,
    CameraAlt as CameraIcon,
    FlipCameraAndroid as FlipIcon,
} from '@mui/icons-material';

const CameraCapture = ({ open, onClose, onCapture, isDark }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const [availableCameras, setAvailableCameras] = useState([]);
    const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
    const isStartingRef = useRef(false);

    // Get available camera devices
    const getAvailableCameras = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setAvailableCameras(videoDevices);
            return videoDevices;
        } catch (err) {
            console.error('Error enumerating devices:', err);
            return [];
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => {
                track.stop();
            });
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    const startCamera = useCallback(async (useFallback = true) => {
        // Prevent multiple simultaneous start attempts
        if (isStartingRef.current) {
            console.log('Camera start already in progress, skipping...');
            return;
        }

        isStartingRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            // Stop any existing stream first
            stopCamera();

            // Get available cameras
            const cameras = await getAvailableCameras();
            
            let constraints = {};
            
            // Try with specific camera device if available
            if (cameras.length > 0 && currentCameraIndex < cameras.length) {
                const selectedCamera = cameras[currentCameraIndex];
                console.log('Using specific camera:', selectedCamera.label);
                constraints = {
                    video: {
                        deviceId: { exact: selectedCamera.deviceId }
                    }
                };
            } else {
                // Fallback to basic video constraints
                constraints = { video: true };
            }

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = mediaStream;
            
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                // Wait for the video to be ready
                await new Promise((resolve) => {
                    videoRef.current.onloadedmetadata = () => {
                        resolve();
                    };
                });
                await videoRef.current.play();
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            
            // Try fallback without any constraints if useFallback is true
            if (useFallback) {
                try {
                    console.log('Trying fallback with basic video constraints');
                    const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
                    streamRef.current = fallbackStream;
                    if (videoRef.current) {
                        videoRef.current.srcObject = fallbackStream;
                        await new Promise((resolve) => {
                            videoRef.current.onloadedmetadata = () => {
                                resolve();
                            };
                        });
                        await videoRef.current.play();
                    }
                    setError(null);
                } catch (fallbackErr) {
                    handleCameraError(fallbackErr);
                }
            } else {
                handleCameraError(err);
            }
        } finally {
            setIsLoading(false);
            isStartingRef.current = false;
            setIsSwitching(false);
        }
    }, [currentCameraIndex, stopCamera, getAvailableCameras]);

    const handleCameraError = (err) => {
        if (err.name === 'NotAllowedError') {
            setError('Camera permission denied. Please allow camera access and try again.');
        } else if (err.name === 'NotFoundError') {
            setError('No camera found on this device.');
        } else if (err.name === 'NotReadableError') {
            setError('Camera is already in use by another application.');
        } else if (err.name === 'OverconstrainedError') {
            setError('Camera does not support the requested settings. Trying basic camera mode...');
        } else {
            setError('Unable to access camera. Please check your device settings.');
        }
    };

    // Initial camera start when dialog opens
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                startCamera();
            }, 100);
            return () => clearTimeout(timer);
        } else {
            stopCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]); // Remove startCamera from dependencies to prevent infinite loop

    // Handle camera switching
    const switchCamera = useCallback(async () => {
        if (!isSwitching && !isLoading && availableCameras.length > 1) {
            setIsSwitching(true);
            const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
            setCurrentCameraIndex(nextIndex);
            // Small delay to let state update
            setTimeout(() => {
                startCamera();
            }, 100);
        } else if (!isSwitching && !isLoading) {
            // If only one camera or enumeration failed, just restart
            setIsSwitching(true);
            setTimeout(() => {
                startCamera();
            }, 100);
        }
    }, [isSwitching, isLoading, availableCameras.length, currentCameraIndex, startCamera]);

    const takePhoto = useCallback(() => {
        if (videoRef.current && canvasRef.current && videoRef.current.readyState === 4) {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Draw video frame to canvas
            const context = canvas.getContext('2d');
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Convert canvas to blob
            canvas.toBlob(blob => {
                const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                onCapture(file);
                onClose();
                stopCamera();
            }, 'image/jpeg', 0.9);
        } else {
            setError('Camera not ready. Please wait a moment and try again.');
        }
    }, [onCapture, onClose, stopCamera]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, [stopCamera]);

    // Determine if video should be mirrored (front camera)
    const isFrontCamera = availableCameras[currentCameraIndex]?.label?.toLowerCase().includes('front') || false;

    return (
        <Dialog
            open={open}
            onClose={() => {
                stopCamera();
                onClose();
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    background: alpha(isDark ? '#1e293b' : '#ffffff', 0.95),
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#cbd5e1', 0.3)}`
            }}>
                <Typography variant="h6">Take Photo</Typography>
                <IconButton onClick={() => {
                    stopCamera();
                    onClose();
                }} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, position: 'relative', minHeight: 400 }}>
                {isLoading && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1
                    }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Box sx={{
                        p: 3,
                        textAlign: 'center',
                        color: 'error.main'
                    }}>
                        <Typography color="error">{error}</Typography>
                        <Button 
                            onClick={() => startCamera()} 
                            sx={{ mt: 2 }}
                            variant="contained"
                            size="small"
                        >
                            Retry
                        </Button>
                    </Box>
                )}

                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                        width: '100%',
                        height: 'auto',
                        display: error ? 'none' : 'block',
                        visibility: isLoading ? 'hidden' : 'visible',
                        transform: isFrontCamera ? 'scaleX(-1)' : 'none'
                    }}
                />

                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </DialogContent>

            <DialogActions sx={{
                p: 2,
                justifyContent: 'center',
                gap: 2,
                borderTop: `1px solid ${alpha(isDark ? '#475569' : '#cbd5e1', 0.3)}`
            }}>
                <Button
                    onClick={switchCamera}
                    startIcon={<FlipIcon />}
                    variant="outlined"
                    disabled={isLoading || isSwitching || !!error}
                    sx={{ borderRadius: 2 }}
                >
                    Switch Camera
                </Button>

                <Button
                    onClick={takePhoto}
                    variant="contained"
                    startIcon={<CameraIcon />}
                    disabled={isLoading || !!error}
                    sx={{
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        }
                    }}
                >
                    Capture
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CameraCapture;